/**
 * RoleEditorDialog — Rolle erstellen/bearbeiten mit Permission-Matrix.
 */
import React from 'react';
import { Button } from '../../components/ui/button';
import { Input } from '../../components/ui/input';
import { Label } from '../../components/ui/label';
import { Textarea } from '../../components/ui/textarea';
import {
  Dialog, DialogContent, DialogHeader, DialogTitle,
} from '../../components/ui/dialog';
import {
  Table, TableBody, TableCell, TableHead, TableHeader, TableRow,
} from '../../components/ui/table';
import { Loader2, Eye, Play, Pencil, Trash2 } from 'lucide-react';
import { PERMISSION_AREAS, LEVEL_ORDER, SCOPE_TYPES, defaultPerms } from './constants';

const DOT_CLASSES = {
  none: 'border-gray-400 bg-gray-400',
  view: 'border-blue-500 bg-blue-500',
  execute: 'border-amber-500 bg-amber-500',
  edit: 'border-green-500 bg-green-500',
};

export const RoleEditorDialog = ({
  open, onOpenChange,
  selectedRole, roleForm, setRoleForm,
  saving, onSave, onDelete, onPreview,
}) => {
  const setField = (field, value) => setRoleForm((prev) => ({ ...prev, [field]: value }));
  const setPermLevel = (area, level) =>
    setRoleForm((prev) => ({ ...prev, permissions: { ...prev.permissions, [area]: level } }));

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="max-w-3xl max-h-[90vh] overflow-y-auto">
        <DialogHeader>
          <DialogTitle>{selectedRole ? 'Rolle bearbeiten' : 'Neue Rolle erstellen'}</DialogTitle>
        </DialogHeader>
        <div className="space-y-6 pt-4">
          <div className="grid grid-cols-2 gap-4">
            <div className="space-y-2">
              <Label>Name *</Label>
              <Input
                value={roleForm.name}
                onChange={(e) => setField('name', e.target.value)}
                placeholder="z.B. Teamleiter/in"
                data-testid="input-role-name"
              />
            </div>
            <div className="space-y-2">
              <Label>Farbe</Label>
              <div className="flex items-center gap-2">
                <input type="color" value={roleForm.color}
                  onChange={(e) => setField('color', e.target.value)}
                  className="w-10 h-10 rounded cursor-pointer border" />
                <Input value={roleForm.color}
                  onChange={(e) => setField('color', e.target.value)} className="flex-1" />
              </div>
            </div>
          </div>
          <div className="space-y-2">
            <Label>Beschreibung</Label>
            <Textarea
              value={roleForm.description}
              onChange={(e) => setField('description', e.target.value)}
              placeholder="Kurze Beschreibung der Rolle..."
              rows={2}
            />
          </div>

          <div className="space-y-3">
            <Label>Geltungsbereich</Label>
            <div className="grid grid-cols-2 gap-3">
              {SCOPE_TYPES.map((scope) => {
                const ScopeIcon = scope.icon;
                const isActive = roleForm.scope_type === scope.value;
                return (
                  <div
                    key={scope.value}
                    onClick={() => setField('scope_type', scope.value)}
                    className={`p-3 rounded-lg border-2 cursor-pointer transition-all ${
                      isActive ? 'border-primary bg-primary/5' : 'border-muted hover:border-muted-foreground/30'
                    }`}
                  >
                    <div className="flex items-center gap-2">
                      <ScopeIcon className={`h-4 w-4 ${isActive ? 'text-primary' : 'text-muted-foreground'}`} />
                      <span className="font-medium text-sm">{scope.label}</span>
                    </div>
                    <p className="text-xs text-muted-foreground mt-1">{scope.description}</p>
                  </div>
                );
              })}
            </div>
          </div>

          <div className="space-y-3">
            <Label>Berechtigungen</Label>
            <div className="border rounded-lg overflow-hidden" data-testid="permission-matrix">
              <Table>
                <TableHeader>
                  <TableRow className="bg-muted/50">
                    <TableHead className="w-[200px]">Bereich</TableHead>
                    <TableHead className="text-center w-[100px]">
                      <span className="text-gray-500">Kein Zugriff</span>
                    </TableHead>
                    <TableHead className="text-center w-[100px]">
                      <div className="flex items-center justify-center gap-1">
                        <Eye className="h-3 w-3 text-blue-600" />
                        <span className="text-blue-600">Ansehen</span>
                      </div>
                    </TableHead>
                    <TableHead className="text-center w-[100px]">
                      <div className="flex items-center justify-center gap-1">
                        <Play className="h-3 w-3 text-amber-600" />
                        <span className="text-amber-600">Ausfuehren</span>
                      </div>
                    </TableHead>
                    <TableHead className="text-center w-[100px]">
                      <div className="flex items-center justify-center gap-1">
                        <Pencil className="h-3 w-3 text-green-600" />
                        <span className="text-green-600">Bearbeiten</span>
                      </div>
                    </TableHead>
                  </TableRow>
                </TableHeader>
                <TableBody>
                  {PERMISSION_AREAS.map((pa) => {
                    const AreaIcon = pa.icon;
                    const currentLevel = roleForm.permissions[pa.area] || 'none';
                    return (
                      <TableRow key={pa.area}>
                        <TableCell>
                          <div className="flex items-center gap-3">
                            <div className="w-8 h-8 rounded bg-muted flex items-center justify-center">
                              <AreaIcon className="h-4 w-4 text-muted-foreground" />
                            </div>
                            <div>
                              <p className="font-medium text-sm">{pa.label}</p>
                              <p className="text-xs text-muted-foreground">{pa.desc}</p>
                            </div>
                          </div>
                        </TableCell>
                        {LEVEL_ORDER.map((level) => (
                          <TableCell key={level} className="text-center">
                            <button
                              type="button"
                              onClick={() => setPermLevel(pa.area, level)}
                              data-testid={`perm-${pa.area}-${level}`}
                              className={`w-6 h-6 rounded-full border-2 transition-all mx-auto flex items-center justify-center ${
                                currentLevel === level ? DOT_CLASSES[level] : 'border-gray-300 bg-white hover:border-gray-400'
                              }`}
                            >
                              {currentLevel === level && (
                                <div className="w-2 h-2 rounded-full bg-white" />
                              )}
                            </button>
                          </TableCell>
                        ))}
                      </TableRow>
                    );
                  })}
                </TableBody>
              </Table>
            </div>
          </div>

          <div className="flex gap-2">
            <Button type="button" variant="outline" size="sm" data-testid="btn-all-edit"
              onClick={() => {
                const p = {};
                PERMISSION_AREAS.forEach((a) => { p[a.area] = 'edit'; });
                setRoleForm((prev) => ({ ...prev, permissions: p }));
              }}>Alle: Bearbeiten</Button>
            <Button type="button" variant="outline" size="sm" data-testid="btn-all-view"
              onClick={() => {
                const p = {};
                PERMISSION_AREAS.forEach((a) => { p[a.area] = 'view'; });
                setRoleForm((prev) => ({ ...prev, permissions: p }));
              }}>Alle: Ansehen</Button>
            <Button type="button" variant="outline" size="sm" data-testid="btn-all-none"
              onClick={() => setRoleForm((prev) => ({ ...prev, permissions: defaultPerms() }))}>
              Alle: Kein Zugriff
            </Button>
          </div>

          <div className="flex justify-between pt-4 border-t">
            <div>
              {selectedRole && !selectedRole.is_system && (
                <Button
                  variant="destructive"
                  onClick={() => { onDelete(selectedRole); onOpenChange(false); }}
                  data-testid="btn-delete-role"
                >
                  <Trash2 className="h-4 w-4 mr-2" />
                  {selectedRole.user_count > 0 ? `Trotzdem löschen (${selectedRole.user_count})` : 'Löschen'}
                </Button>
              )}
            </div>
            <div className="flex gap-2">
              {selectedRole && (
                <Button variant="outline" className="gap-2" data-testid="btn-preview-dialog"
                  onClick={() => onPreview({ ...selectedRole, ...roleForm })}>
                  <Eye className="h-4 w-4" /> Vorschau
                </Button>
              )}
              <Button variant="outline" onClick={() => onOpenChange(false)}>Abbrechen</Button>
              <Button onClick={onSave} disabled={saving} data-testid="btn-save-role">
                {saving && <Loader2 className="h-4 w-4 mr-2 animate-spin" />} Speichern
              </Button>
            </div>
          </div>
        </div>
      </DialogContent>
    </Dialog>
  );
};
